import {
  FETCH_CONFIG_SUCCESS,
  FETCH_PUBLIC_CONFIG_SUCCESS,
  FETCH_CONFIG_UPDATE_SUCCESS,
  FETCH_CONFIG_ERROR,
  FETCH_PUBLIC_CONFIG_ERROR
} from '@components/config/constants';
import moment from 'moment-timezone';
import { BASE_URL } from '@constants/endpoints';
import axios from 'axios';
import { getServerErrorAction } from '@actions/shared-actions';
import { mergeConfig, setConfig } from '@utils/config-utils';
import { applyEntityConfig } from '@components/config/v2/apply_translated_v2_config';
import { stripEntityConfig } from '@components/config/v2/strip_v1_config';

import { getConfig } from './selectors';

const IGNORE_PATHS = [
  /^api\.serializer\.extra_kwargs/,
  /^autocomplete\.map/,
  /^batch\.[^.]*/,
  /^entity_types\[[^\]]*\]\.config\.fields\.[^.]*\.validation/
];

const IGNORE_ORDER = [
  /^entity_types\[[0-9]*\]\.config\.categories/
];

const diff = (origVal, newVal, path) => {
  if (IGNORE_PATHS.some(match => path.match(match))) {
    return [];
  }

  const lines = [];
  const isObj = val => val !== null && typeof val === 'object' && !Array.isArray(val);

  if (isObj(origVal) && isObj(newVal)) {
    const allKeys = new Set([...Object.keys(origVal), ...Object.keys(newVal)]);
    for (const key of allKeys) {
      const keyPath = path ? `${path}.${key}` : key;
      if (IGNORE_PATHS.some(match => keyPath.match(match))) {
        continue;
      }
      if (!(key in origVal)) {
        lines.push(`+ ${keyPath}: ${JSON.stringify(newVal[key])}`);
      } else if (!(key in newVal)) {
        lines.push(`- ${keyPath}: ${JSON.stringify(origVal[key])}`);
      } else {
        lines.push(...diff(origVal[key], newVal[key], keyPath));
      }
    }
  } else if (Array.isArray(origVal) && Array.isArray(newVal)) {
    if (IGNORE_ORDER.some(match => path.match(match))) {
      const sorted = arr => [...arr].map(item => JSON.stringify(item)).sort();
      if (JSON.stringify(sorted(origVal)) !== JSON.stringify(sorted(newVal))) {
        lines.push(`- ${path}: ${JSON.stringify(origVal)}`);
        lines.push(`+ ${path}: ${JSON.stringify(newVal)}`);
      }
    } else {
      const len = Math.max(origVal.length, newVal.length);
      for (let idx = 0; idx < len; idx += 1) {
        const itemPath = `${path}[${idx}]`;
        if (idx >= origVal.length) {
          lines.push(`+ ${itemPath}: ${JSON.stringify(newVal[idx])}`);
        } else if (idx >= newVal.length) {
          lines.push(`- ${itemPath}: ${JSON.stringify(origVal[idx])}`);
        } else {
          lines.push(...diff(origVal[idx], newVal[idx], itemPath));
        }
      }
    }
  } else if (JSON.stringify(origVal) !== JSON.stringify(newVal)) {
    lines.push(`- ${path}: ${JSON.stringify(origVal)}`);
    lines.push(`+ ${path}: ${JSON.stringify(newVal)}`);
  }

  return lines;
};

const diffObjects = (original, updated) => diff(original, updated, '');

const logV2ConfigDifferences = (originalData, newData) => {
  const lines = diffObjects(originalData, newData);
  if (lines.length === 0) {
    return;
  }

  // eslint-disable-next-line no-console
  console.group('[v2 config diff]');
  lines.forEach(line => {
    const color = line.startsWith('-') ? '#f87171' : '#4ade80';
    // eslint-disable-next-line no-console
    console.log(`%c${line}`, `color: ${color}`);
  });
  // eslint-disable-next-line no-console
  console.groupEnd();
  // eslint-disable-next-line no-console
  console.log(originalData);
  // eslint-disable-next-line no-console
  console.log(newData);
};

const fetchConfigSuccessAction = config => ({ type: FETCH_CONFIG_SUCCESS, config });
const fetchPublicConfigSuccessAction = config => ({ type: FETCH_PUBLIC_CONFIG_SUCCESS, config });
const fetchConfigUpdateSuccessAction = config => ({ type: FETCH_CONFIG_UPDATE_SUCCESS, config });
const fetchConfigErrorAction = () => ({ type: FETCH_CONFIG_ERROR });
const fetchPublicConfigErrorAction = () => ({ type: FETCH_PUBLIC_CONFIG_ERROR });

export const fetchConfig = () => async dispatch => {
  const v1Request = axios.get(`${BASE_URL}/api/public/config/`);

  let v1Response;
  try {
    v1Response = await v1Request;
  } catch (error) {
    dispatch(getServerErrorAction('config', error));
    dispatch(fetchConfigErrorAction());
    return;
  }

  const config = v1Response.data;
  moment.tz.setDefault(config.timezoneName);
  setConfig(config);
  dispatch(fetchConfigSuccessAction(config));
};

export const fetchPublicConfig = (categoryValues) => async (dispatch, getState) => {
  // Entity config
  const v2Request = axios.get(`${BASE_URL}/api/public/config_v2/entity_type/`);
  let v2Response = null;
  try {
    v2Response = await v2Request;
  } catch (err) {
    if (err.response?.status !== 404) {
      dispatch(getServerErrorAction('config', err));
      dispatch(fetchPublicConfigErrorAction());
      return;
    }
  }
  const existingConfig = getConfig(getState());
  const strippedConfig = stripEntityConfig(existingConfig);
  let publicConfig;
  if (Array.isArray(v2Response?.data)) {
    publicConfig = applyEntityConfig(strippedConfig, v2Response.data, {}, categoryValues, true);
  } else {
    publicConfig = existingConfig;
  }

  // Save public config
  dispatch(fetchPublicConfigSuccessAction({
    details: publicConfig.details,
    entity_types: publicConfig.entity_types
  }));
};

export const fetchConfigUpdate = (agencyTypes, categoryValues) => async (dispatch, getState) => {
  const updateRequest = axios.get(`${BASE_URL}/api/config_update/`);
  const v2Request = axios.get(`${BASE_URL}/api/config_v2/entity_type/`);

  let updateResponse;
  try {
    updateResponse = await updateRequest;
  } catch (error) {
    dispatch(getServerErrorAction('config', error));
    dispatch(fetchConfigErrorAction());
    return;
  }

  let v2Response = null;
  try {
    v2Response = await v2Request;
  } catch (err) {
    if (err.response?.status !== 404) {
      dispatch(getServerErrorAction('config', err));
      dispatch(fetchConfigErrorAction());
      return;
    }
  }

  const existingConfig = getConfig(getState());
  const updatedConfig = {...existingConfig, ... updateResponse.data};

  let config;
  if (Array.isArray(v2Response?.data)) {
    config = applyEntityConfig(stripEntityConfig(updatedConfig), v2Response.data, agencyTypes, categoryValues);
    if (process.env.NODE_ENV !== 'production') {
      logV2ConfigDifferences(updatedConfig, config);
    }
  } else {
    config = updatedConfig;
  }

  mergeConfig(config);
  dispatch(fetchConfigUpdateSuccessAction(config));
};
